import React from 'react';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  createPartnerTransfersRoute,
  createPartnerTransfersCreateRoute,
} from 'adminConstants/routes';
import { Edit, Delete, HandshakeOutlined } from '@mui/icons-material';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { ActionStatus } from 'further-types/lp/action';
import Page from 'components/new/Page';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure } from 'further-ui/hooks';

const PartnerTransfersListContent: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const deleteConfirmationDialog = useDisclosure<{ id: string }>();
  const [canEdit, canDelete] = useHasPermissions([
    RolePermission.LpEditPartnerTransfer,
    RolePermission.LpDeletePartnerTransfer,
  ]);
  const [partnerTransfersData, query] =
    api.partnerTransfers.list.useSuspenseQuery({
      vehicleId: vehicle?._id ?? '',
    });

  const mutationOpts = useHandleTrpcMutation<{
    success: boolean;
  }>();

  const deletePartnerTransfer = api.partnerTransfers.delete.useMutation(
    mutationOpts({
      successMessage: 'Partner transfer deleted successfully.',
      onSuccess: deleteConfirmationDialog.onClose,
    }),
  );

  const partnerTransfers = partnerTransfersData?.results ?? [];

  const columns: Array<TableColumn<(typeof partnerTransfers)[number]>> = [
    {
      key: 'transferDate',
      label: 'Transfer date',
      render: ({ transferDate, id }) => (
        <ColorIndicatorWithText
          text={transferDate.toReadable()}
          colorKey={{ id }}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: ({ status }) =>
        status === ActionStatus.Draft ? 'Draft' : 'Approved',
    },
    {
      key: 'transferees',
      label: 'Transferees',
      render: ({ transferees }) => transferees.length,
    },
  ];

  return (
    <>
      <Table
        emptyMessage="No partner transfers found."
        actions={[
          {
            label: 'Edit',
            icon: Edit,
            link: ({ id }) => createPartnerTransfersRoute(id ?? ''),
            disabled: ({ isDraft }) => !isDraft,
            hidden: () => !canEdit,
          },
          {
            label: 'Delete',
            icon: Delete,
            onClick: ({ id }) =>
              deleteConfirmationDialog.stageAction({ id: id ?? '' }),
            disabled: ({ canBeDeleted }) => !canBeDeleted,
            hidden: () => !canDelete,
          },
        ]}
        onRowClickHref={({ id, isDraft }) =>
          canEdit && isDraft ? createPartnerTransfersRoute(id ?? '') : undefined
        }
        columns={columns}
        isLoading={query.isFetching}
        rows={partnerTransfers}
        rowsTotalCount={partnerTransfers.length ?? 0}
      />
      <ConfirmDialog
        actions={{
          confirm: {
            onClick: () => {
              const partnerTransferId =
                deleteConfirmationDialog.actionProps?.id;
              if (!partnerTransferId) return;
              deletePartnerTransfer.mutate({
                partnerTransferId,
                vehicleId: vehicle?._id ?? '',
              });
            },
            label: 'Delete',
            variant: 'contained',
            color: 'error',
            disabled: deletePartnerTransfer.isPending,
          },
          cancel: { onClick: deleteConfirmationDialog.onClose },
        }}
        open={deleteConfirmationDialog.isOpen}
        title="Delete partner transfer"
        content="Are you sure you want to delete this partner transfer? This action cannot be undone."
      />
    </>
  );
};

const ListPartnerTransfers: React.FC = () => {
  const [canCreate] = useHasPermissions([
    RolePermission.LpCreatePartnerTransfer,
  ]);

  return (
    <Page
      spacing="lg"
      heading="Partner Transfers"
      headingIcon={HandshakeOutlined}
      withHeadingDivider
      actions={[
        {
          label: 'Create Partner Transfer',
          link: createPartnerTransfersCreateRoute(),
          hidden: !canCreate,
        },
      ]}
    >
      <PartnerTransfersListContent />
    </Page>
  );
};

export default ListPartnerTransfers;
